import React from 'react';
import type { SwitchReport } from '@/src/modules/wizard/schema/switch-template';

export interface SwitchTemplateDialogProps {
  isOpen: boolean;
  targetTemplateName: string;
  report: SwitchReport;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SwitchTemplateDialog: React.FC<SwitchTemplateDialogProps> = ({
  isOpen,
  targetTemplateName,
  report,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const { carriedOver, parked, missing } = report;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-slate-900/95 p-6 shadow-2xl text-slate-100 space-y-6">
        <div>
          <h3 className="text-xl font-bold text-white tracking-tight">
            Switching to &quot;{targetTemplateName}&quot;
          </h3>
          <p className="mt-1 text-sm text-slate-400">
            Review how your details will be mapped to this template.
          </p>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* Section 1: Carried Over */}
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
            <div className="flex items-center gap-2 font-semibold text-emerald-400 text-sm">
              <span className="text-base">✅</span>
              <span>Carried over</span>
            </div>
            {carriedOver.length > 0 ? (
              <p className="mt-1.5 text-xs text-emerald-200/80 leading-relaxed">
                {carriedOver.join(', ')}
              </p>
            ) : (
              <p className="mt-1 text-xs text-emerald-200/50 italic">
                Basic event details (date, venue, host) will carry over.
              </p>
            )}
          </div>

          {/* Section 2: Parked / Won't be shown */}
          {parked.length > 0 && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4">
              <div className="flex items-center gap-2 font-semibold text-amber-400 text-sm">
                <span className="text-base">📦</span>
                <span>Won&apos;t be shown in this template</span>
              </div>
              <p className="mt-1.5 text-xs text-amber-200/80 leading-relaxed">
                {parked.join(', ')}
              </p>
              <p className="mt-2 text-[11px] text-amber-300/60 font-medium">
                Saved safely — these will come back automatically if you switch back.
              </p>
            </div>
          )}

          {/* Section 3: Missing / Template asks for */}
          {missing.length > 0 && (
            <div className="rounded-xl border border-sky-500/20 bg-sky-500/10 p-4">
              <div className="flex items-center gap-2 font-semibold text-sky-400 text-sm">
                <span className="text-base">➕</span>
                <span>This template asks for</span>
              </div>
              <p className="mt-1.5 text-xs text-sky-200/80 leading-relaxed">
                {missing.join(', ')}
              </p>
              <p className="mt-2 text-[11px] text-sky-300/60 font-medium">
                You can fill these in after switching.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/20 hover:from-amber-400 hover:to-amber-500 transition-all active:scale-[0.98]"
          >
            Confirm &amp; Switch
          </button>
        </div>
      </div>
    </div>
  );
};
